import { type ReactNode, useEffect, useState } from "react";
import { setDeviceAuthTokenGetter } from "@workspace/api-client-react";
import { DeviceProvider } from "@/player/context/device-context";
import {
  KioskExitProvider,
  KioskRouteGuard,
} from "@/player/hooks/kiosk-exit-context";
import {
  getActiveScreenCredential,
  readStoredCredential,
} from "@/player/lib/screen-credential";

function resolveDeviceAuthToken(): string | null {
  // custom-fetch only attaches this bearer to `/api/device/*` paths, so the
  // stored credential is safe to expose on every route. Restricting the
  // stored-credential fallback to /player left DeviceProvider's device-info
  // query unauthenticated on control pages, spamming 401 "Missing screen
  // credential" (Sentry OMNIMEDIA-WEB-3).
  return getActiveScreenCredential() ?? readStoredCredential();
}

/**
 * Wraps the app with device context and wires the screen bearer into device API
 * routes. Playback runs only on `/player` (via PlayerRuntime), not here.
 */
export function PersistentPlayerRoot({ children }: { children: ReactNode }) {
  // Register synchronously during the first render, not in a passive effect:
  // DeviceProvider (a child) subscribes its /api/device/info query in the
  // commit phase, which runs before this parent's effects — the first request
  // went out with no bearer, 401'd, and release() wiped the credential and the
  // whole query cache (the intermittent dead /start cards).
  useState(() => {
    setDeviceAuthTokenGetter(() => resolveDeviceAuthToken());
  });

  useEffect(() => {
    // Re-register on mount so a StrictMode simulated unmount (which runs the
    // cleanup below) doesn't leave the getter null, and clean up for real when
    // the tree unmounts.
    setDeviceAuthTokenGetter(() => resolveDeviceAuthToken());
    return () => setDeviceAuthTokenGetter(null);
  }, []);

  return (
    <DeviceProvider>
      <KioskExitProvider>
        <KioskRouteGuard />
        {children}
      </KioskExitProvider>
    </DeviceProvider>
  );
}
